// CUSTOM: personalised-review-manager.js
// Review page orchestrator. PR-3.1: single consolidated init entry point.
// Coordinates: reviewData, reviewRenderer, reviewActions, eventHandlers.
// All data-loading, rendering, and action logic is delegated to focused modules:
//   personalised-review-data.js     � data loading
//   personalised-review-renderer.js � UI rendering and content
//   personalised-review-actions.js  � button management and action handlers
//   personalised-event-handlers.js  � interactive event listeners (frequency/remove/variant)
//= require personalised-config.js
//= require personalised-utils.js
//= require personalised-bundle-state.js
//= require personalised-bundle-utils.js
//= require personalised-bundle-cart-sync.js
//= require personalised-bundle-ui.js
//= require personalised-event-handlers.js
//= require personalised-validation.js
//= require personalised-review-data.js
//= require personalised-review-renderer.js
//= require personalised-review-actions.js

(function() {
  // Ensure namespace exists
  window.personalised = window.personalised || {};

  // Get config reference
  const config = window.personalised.config;

  // =============================================================================
  // ===== EDIT MODE ONLY � MODE-SPECIFIC INIT FLOWS =====
  // =============================================================================

  /**
   * [EDIT MODE ONLY] Runs the full edit-mode init sequence.
   */
  async function runEditModeInit() {
    // Spinner guard for Recharge bundle fetch (safe to call � guarded internally)
    var st = window.personalised && window.personalised.__spinnerState;
    if (!st || !st.shown) {
      window.personalised.modalSpinner?.show?.('Loading your pack�');
      if (st) {
        st.shown = true;
        st.t0 = (performance && performance.now ? performance.now() : Date.now());
      }
    }
    if (window.personalised?.debugMode) console.debug('[Review] Processing EDIT mode flow with valid mode');

    // Parallelise product data fetch and subscription data init
    const [_, subscriptionData] = await Promise.all([
      window.personalised.reviewData.waitForProductDataReady(),
      window.personalised.reviewData.initSubscriptionData()
    ]);
    window.personalised.reviewRenderer.updateUIForMode();
    window.personalised.reviewActions.initCheckoutButton(); // PR-3.1: init checkout buttons directly after mode UI is set
    window.personalised?.debug?.time('review:loadBundleData');
    await window.personalised.reviewData.loadBundleData();
    window.personalised?.debug?.timeEnd('review:loadBundleData');

    // Ensure variant lookup is current before final render
    if (window.personalised.bundleUI?.ensureVariantLookupBuilt) {
      if (window.personalised?.debugMode) console.log('Building variant lookup before rendering in EDIT mode...');
      window.personalised.bundleUI.ensureVariantLookupBuilt();
    }
    window.personalised.reviewActions.initAfterPayVisibility(); // PR-3.1: init Afterpay after state is loaded
  }

  /**
   * [GUEST/NEW MODE ONLY] Runs the full guest/new-mode init sequence.
   * @param {string} mode - The current mode string ('guest' or 'new').
   */
  async function runNonEditModeInit(mode) {
    if (window.personalised?.debugMode) console.debug(`[Review] Processing ${mode} mode flow with valid mode`);

    // 1. Load bundle state from localStorage
    window.personalised.bundle.load();

    // 2. Wait for product data only if the bundle has items to render
    if (window.personalised.validation.bundleHasItems()) {
      await window.personalised.reviewData.waitForProductDataReady();
    }

    // 3. Build variant lookup and emit state-loaded events
    if (window.personalised.bundleUI?.ensureVariantLookupBuilt) {
      if (window.personalised?.debugMode) console.log('Building variant lookup before rendering in GUEST/NEW mode...');
      window.personalised.bundleUI.ensureVariantLookupBuilt();
      window.personalised.eventBus.emit('bundleStateLoaded');
      window.personalised.eventBus.emit('variantLookupBuilt');
    } else if (window.personalised.bundleUI?.buildVariantLookup) {
      // Fallback if ensureVariantLookupBuilt is not available
      window.personalised.bundleUI.buildVariantLookup();
      window.personalised.eventBus.emit('bundleStateLoaded');
      window.personalised.eventBus.emit('variantLookupBuilt');
    }

    window.personalised.reviewRenderer.updateUIForMode();
    window.personalised.reviewActions.initCheckoutButton();    // PR-3.1: init checkout buttons directly after mode UI is set
    window.personalised.reviewActions.initAfterPayVisibility(); // PR-3.1: init Afterpay after state is loaded
  }

  // =============================================================================
  // ===== ORCHESTRATOR =====
  // =============================================================================

  /**
   * [ORCHESTRATOR] Initializes the review page with proper mode detection and sequencing.
   * PR-3.1: single entry point � called once from DOMContentLoaded in the template.
   * PR-3.2: mode-specific work is delegated to runEditModeInit / runNonEditModeInit.
   */
  async function initReviewPage() {
    // Yield a frame to let the browser paint the spinner before heavy work
    if (typeof requestAnimationFrame === 'function') {
      await new Promise(requestAnimationFrame);
    }
    window.personalised?.debug?.time('review:initReviewPage');
    window.personalised?.debug?.trace('review:initReviewPageStart', { ts: Date.now() });

    // Show spinner immediately on page arrival for all modes (guard against duplicate)
    let spinnerStartTime = Date.now();
    var st = window.personalised && window.personalised.__spinnerState;
    if (!st || !st.shown) {
      window.personalised.modalSpinner?.show?.('Loading your pack�');
      if (st) {
        st.shown = true;
        st.t0 = (performance && performance.now ? performance.now() : Date.now());
      }
    }
    try {
      // Listen for login state changes (informational log only � review page inits fresh on each load).
      window.addEventListener('personalised:loginStateChanged', function(event) {
        if (window.personalised?.debugMode) console.debug('[Review Manager] Login state changed:', event.detail);
      });

      // Check current login state directly
      const isLoggedIn = window.personalisedCore?.loginTracking?.isUserLoggedIn() ||
                        (document.body?.dataset?.customerLoggedIn === 'true');
      if (window.personalised?.debugMode) console.debug('[Review Manager] Current login state:', isLoggedIn);

      const currentMode = window.personalised.utils.getMode();

      // Only validate critical metadata in edit mode to avoid unnecessary warnings
      if (currentMode === window.personalised.config.modes.EDIT) {
        if (window.personalised?.debugMode) console.log('[Review Page] Validating metadata in edit mode...');

        // Check metadata integrity and recover if needed
        if (window.personalised.utils?.validateMetadataIntegrity) {
          const metadataValid = await window.personalised.utils.validateMetadataIntegrity();
          if (!metadataValid) {
            if (window.personalised?.debugMode) console.warn('[Review Page] Metadata integrity check failed - recovery attempted');
          }
        }

        // Continue with metadata integrity validation for logging purposes
        if (!window.personalised.bundle.getMetadata('bundleSelectionId')) {
          if (window.personalised?.debugMode) console.error('[Review Page] Missing bundleSelectionId � bundle updates may not work correctly.');
        }

        if (!window.personalised.bundle.getMetadata('subscriptionId')) {
          if (window.personalised?.debugMode) console.error('[Review Page] Missing subscriptionId � subscription features may not work correctly.');
        }

        if (!window.personalised.bundle.getMetadata('purchaseItemId')) {
          if (window.personalised?.debugMode) console.error('[Review Page] Missing purchaseItemId � bundle updates may fail.');
        }
      } else {
        if (window.personalised?.debugMode) console.log(`[Review Page] Skipping metadata validation in ${currentMode} mode`);
      }

      // Define the render hooks for bundle state to use
      window.personalised.renderHooks = {
        renderBundleItems: () => {
          if (window.personalised.bundleUI?.renderBundleItems) {
            window.personalised.bundleUI.renderBundleItems();
          }
        }
      };

      // Show initial loading state
      const listEl = document.getElementById('bundle-items');
      if (listEl) {
        listEl.innerHTML = `<p>${window.personalised.config.uiLabels.loading}</p>`;
      }

      // CRITICAL: Wait for mode initialization to complete before proceeding
      window.personalised?.debug?.time('review:initMode');
      await window.personalised.utils.initMode();
      window.personalised?.debug?.timeEnd('review:initMode');
      if (window.personalised.debugMode) console.debug('[Review] Mode initialization complete');

      // Now safely get the mode after initialization is guaranteed
      const mode = window.personalised.utils.getMode();
      window.personalised?.debug?.trace('review:modeDetected', { mode: mode });

      // PR-5.1 / PR-5.2: Split routing.
      // When splitReviewExperience is false this block is a complete no-op (rollback path).
      // When true: redirect the user to the correct page for their mode if they are on the wrong one.
      //   guest / new  → /pages/personalised-review
      //   edit         → /pages/manage-pack
      // Uses window.location.replace() so the wrong page is not added to browser history.
      // Returns immediately after triggering the redirect to abort further page init.
      if (config.features?.splitReviewExperience) {
        const _currentPath = window.location.pathname;
        const _managePath  = config.routes.managePack; // '/pages/manage-pack'
        const _reviewPath  = config.routes.review;     // '/pages/personalised-review'
        const _intendedPath = (mode === config.modes.EDIT) ? _managePath : _reviewPath;

        if (_currentPath !== _intendedPath) {
          window.personalised?.debug?.trace('review:routingRedirect', { from: _currentPath, to: _intendedPath, mode: mode });
          window.location.replace(_intendedPath);
          return; // Abort init — navigation is in progress
        }
      }

      // PR-1.2: Surface cancelled-subscription state signal on the review wrapper immediately
      window.personalised.reviewRenderer.surfaceSubscriptionStateSignal();
      // Show checkout disclaimers for non-edit modes as early as possible (PR-3.1)
      window.personalised.reviewActions.initDisclaimerVisibility(mode);
      const subscriptionId = window.personalised.utils.getCurrentSubscriptionId();
      // PR-4.3: route through storageManager (single authority for SESSION_FRESH key)
      const isSessionFresh = window.personalised.storageManager.isSessionFresh();
      if (window.personalised?.debugMode) console.log(`Initializing in ${mode} mode with fresh session: ${isSessionFresh}`);

      // Start performance measurement if available
      if (window.personalised.bundleUI?.initPerformanceUtils) {
        window.personalised.bundleUI.initPerformanceUtils();
      }

      // Set up event listeners early (but don't render yet)
      if (window.personalised.eventHandlers?.initAllHandlers) {
        window.personalised.eventHandlers.initAllHandlers();
      }
      window.personalised?.debug?.trace('review:setupEventListeners', {});
      setupEventListeners();

      // PR-3.2: Invoke the mode-specific init flow via clearly named helpers.
      if (mode === config.modes.EDIT) {
        await runEditModeInit();
      } else if (mode) {
        await runNonEditModeInit(mode);
      } else {
        // This should never happen if initMode worked correctly
        if (window.personalised?.debugMode) console.error('[Review] Failed to get valid mode after initialization');
      }

      // Final render after all data is ready, only if we have a valid mode
      if (mode) {
        window.personalised?.debug?.time('review:renderBundleItems');
        const renderSuccess = window.personalised.reviewRenderer.renderBundleItems();
        window.personalised?.debug?.timeEnd('review:renderBundleItems');
        // PR-3.1: update personalised content after render
        window.personalised.reviewRenderer.updatePersonalisedHeading();
        window.personalised.reviewRenderer.updateReviewIntroQuiz();
        if (renderSuccess) {
          window.personalised.eventBus.emit('renderComplete');
          // Schedule UI settled check for spinner timing
          if (window.personalised.bundleUI?.whenUiSettled) {
            window.personalised.bundleUI.whenUiSettled().then(() => {
              window.personalised?.debug?.trace('review:uiSettled', { ts: Date.now(), totalInitDuration: window.personalised._reviewInitTs ? Date.now() - window.personalised._reviewInitTs : null });
              window.personalised.eventBus.emit('uiSettled');
            });
          } else {
            // Fallback if whenUiSettled not available
            window.personalised?.debug?.trace('review:uiSettled', { ts: Date.now(), totalInitDuration: window.personalised._reviewInitTs ? Date.now() - window.personalised._reviewInitTs : null });
            window.personalised.eventBus.emit('uiSettled');
          }
          // Note: spinner hide moved to uiSettled event listener
        }

        // Background cart sync for guest and new modes
        if (mode !== config.modes.EDIT) {
          window.personalised.utils?.refreshCartFromState?.().catch(err => {
            if (window.personalised?.debugMode) console.warn('Background cart sync failed:', err);
          });
        }
      }
      window.personalised?.debug?.timeEnd('review:initReviewPage');

    } catch (err) {
      window.personalised?.debug?.timeEnd('review:initMode');
      window.personalised?.debug?.timeEnd('review:loadBundleData');
      window.personalised?.debug?.timeEnd('review:initReviewPage');
      if (window.personalised?.debugMode) console.error('Error during review page initialization:', err);
      // Re-query the element to avoid scope issues
      const errorListEl = document.getElementById('bundle-items');
      if (errorListEl) {
        errorListEl.innerHTML = '<p>An error occurred while loading your bundle. Please refresh the page.</p>';
      }
    }
  }

  // =============================================================================
  // ===== SHARED � SETUP AND EVENT BINDING =====
  // =============================================================================

  /**
   * [SHARED] Sets up event listeners for bundle state changes.
   * Wires up spinner-hide on uiSettled, bundleStateLoaded for render and checkout-button re-init,
   * and resize handler for checkout-button visibility.
   */
  function setupEventListeners() {
    const eventHandlers = {
      onStateChange: function() {
        window.personalised.reviewRenderer.renderBundleItems();
      }
    };

    // Set up one-shot spinner hide on uiSettled (when UI is visually ready)
    (function setupSpinnerHideOnReady() {
      var bus = window.personalised && window.personalised.eventBus;
      if (!bus || !bus.on) return;

      function onUiSettled() {
        try {
          var st = window.personalised && window.personalised.__spinnerState;
          var now = (performance && performance.now ? performance.now() : Date.now());
          var t0  = (st && st.t0) || now;
          var elapsed = Math.max(0, now - t0);
          var remaining = Math.max(0, 2000 - elapsed);

          if (st && st.fallbackId) {
            clearTimeout(st.fallbackId);
            st.fallbackId = null;
          }

          var hide = function () {
            try { window.personalised.modalSpinner?.hide?.(); } catch (e) {}
          };
          if (remaining === 0) hide(); else setTimeout(hide, remaining);
        } finally {
          try { bus.off && bus.off('uiSettled', onUiSettled); } catch (e) {}
        }
      }

      bus.on('uiSettled', onUiSettled);
    })();

    // Subscribe to events
    if (window.personalised?.eventBus && typeof window.personalised.eventBus.on === 'function') {
      window.personalised.eventBus.on('bundleStateLoaded', eventHandlers.onStateChange);
      // Re-init checkout button visibility whenever bundle state is (re-)loaded
      window.personalised.eventBus.on('bundleStateLoaded', window.personalised.reviewActions.initCheckoutButton);
    } else {
      if (window.personalised?.debugMode) console.warn('[Review Manager] Event bus not initialized, skipping event binding');
    }

    // Re-evaluate checkout button positions/visibility on viewport resize
    let checkoutResizeTimeout;
    window.addEventListener('resize', () => {
      clearTimeout(checkoutResizeTimeout);
      checkoutResizeTimeout = setTimeout(() => {
        if (window.personalised?.debugMode) console.log('Window resized, checking checkout buttons');
        window.personalised.reviewActions.initCheckoutButton();
      }, 250);
    });
  }

  // Create the review manager module
  const reviewManager = {
    /**
     * Initializes the review page functionality
     */
    init: initReviewPage,
    /**
     * Tears down the review manager instance
     */
    teardown: function() {
      // Clean up render hooks
      if (window.personalised.renderHooks?.renderBundleItems) {
        delete window.personalised.renderHooks.renderBundleItems;
      }
    }
  };

  // Assign to single namespace
  window.personalised.reviewManager = reviewManager;
})();

// NOTE: All event handling for [data-add-to-pack] is now delegated in personalised-bundle-manager.js
// Use data-* attributes for any future selectors or event logic
